// poiesis/src/views/contract.ts
// SEN-633: the GENERATED contract is the single source of truth for TYPES
// (`src/api/contract.generated.ts`, codegen'd from monad/poiesis/contract.py —
// do not hand-edit those types). This file re-exports them and keeps the RUNTIME
// Zod validators (the generated file is types-only). Drift between the Zod and the
// generated contract is caught by `conformance.test.ts`.
import { z } from 'zod'
import type { Measure1 } from '../api/contract.generated'

// ── Canonical TYPES (generated; SEN-633) ────────────────────────────────────
export type {
  ReduceRequest,
  Reducer,
  Scope,
  IdentityReducer,
  ScalarReducer,
  GroupByReducer,
  TimeBucketReducer,
} from '../api/contract.generated'
export type { ReducedFrame } from '../api/contract.frame.generated'

// HopPath is a consumer-only convenience (picker → attribute), NOT a wire field.
export type HopPath = { def: string; attr: string; sub_field?: string }[]

// ── Runtime vocabularies (mirror contract.py) ───────────────────────────────
// The request enums below are the SUBSET this GUI sends (GATE: the server's
// full vocabulary — percentile, window ops, year / 15min … — is derived from
// the engine registries and answered on /v1/.../queries); the FRAME enums
// (WarningKind, DroppedReason) must accept every server member, locked by
// views/contract.parity.test.ts against the emitted frame schema.
export const ScalarAgg = z.enum(['sum', 'avg', 'min', 'max', 'count', 'first', 'last'])
export const BucketAgg = z.enum(['sum', 'avg', 'min', 'max', 'first', 'last'])
export const Granularity = z.enum(['hour', 'day', 'week', 'month'])
// SEN-719: parity with the GENERATED contract union (api/contract.generated.ts
// Op1) is locked by views/contract.parity.test.ts against the emitted JSON
// schema — this hand list cannot silently drift from the server's.
export const FilterLeafOp = z.enum(['eq', 'ne', 'lt', 'gt', 'lte', 'gte', 'in', 'not_in', 'is_set', 'is_not_set', 'contains'])
export const WarningKind = z.enum([
  'sparse_input', 'stale_input', 'method_downgrade', 'incomplete_coverage',
  'extrapolation_beyond_horizon', 'incompatible_sum', 'unknown_unit',
  'unit_unverified', 'applies_to_mismatch',
  // SEN-1444: a mark the target view kind cannot draw was stripped
  // server-side (monad/poiesis/marks.py) — the frame says so truthfully.
  'unsupported_mark',
  // GATE: an inline-formula measure; explain's compile refusal; an answer
  // served from the result cache (stage 5).
  'ad_hoc', 'plan_refused', 'cached',
])
export const Severity = z.enum(['info', 'warning', 'alert'])
export const DroppedReason = z.enum([
  'high_cardinality', 'page_cap', 'missing_value', 'unresolved_dimension',
  // I1: a deliberate top/bottom-N cut (GAP B rank), never a cardinality
  // overflow — mirrors monad/poiesis/contract.py.
  'ranked',
  // GATE: rows a measure condition (`having`) removed.
  'having',
])

// ── Filter tree (recursive) ──────────────────────────────────────────────────
export const FilterLeaf = z.object({
  attribute: z.string(),
  op: FilterLeafOp,
  value: z.unknown(),
})
const FilterLeafSchema = FilterLeaf
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FilterAndSchema: z.ZodType<any> = z.lazy(() => z.object({ and: z.array(FilterClauseSchema) }))
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FilterOrSchema: z.ZodType<any> = z.lazy(() => z.object({ or: z.array(FilterClauseSchema) }))
export const FilterClauseSchema: z.ZodType<unknown> = z.lazy(() =>
  z.union([FilterAndSchema, FilterOrSchema, FilterLeafSchema]),
)
export type FilterClause = z.infer<typeof FilterLeaf> | { and: FilterClause[] } | { or: FilterClause[] }

// ── Reducer validators — discriminated union on `kind` ───────────────────────
export const IdentityReducerSchema = z.object({
  kind: z.literal('identity'),
  columns: z.array(z.string()).nullable().optional(),
})
export const ScalarReducerSchema = z.object({
  kind: z.literal('scalar'),
  agg: ScalarAgg,
})
export const GroupByReducerSchema = z.object({
  kind: z.literal('group-by'),
  agg: ScalarAgg,
  dimensions: z.array(z.string()).min(1),
})
export const TimeBucketReducerSchema = z.object({
  kind: z.literal('time-bucket'),
  agg: BucketAgg,
  granularity: Granularity,
  tz: z.string().nullable().optional(),
  // SEN-1428: optional series dimension(s) — at most ONE renders in v1; the
  // reducer annotates-not-blocks and ignores extras beyond the first.
  dimensions: z.array(z.string()).nullable().optional(),
  // GAP B: the engine's post-order over the frame it just built.
  rank: z.object({ by: z.string(), n: z.number().int().min(1).max(1000),
                   order: z.enum(['asc', 'desc']) }).nullable().optional(),
})
export const ReducerSchema = z.discriminatedUnion('kind', [
  IdentityReducerSchema,
  ScalarReducerSchema,
  GroupByReducerSchema,
  TimeBucketReducerSchema,
])

// ── Scope + ReduceRequest validators ─────────────────────────────────────────
export const CompareKind = z.enum(['previous_period', 'same_period_last_year', 'previous_days', 'same_days_last_year'])
export const ComparePeriodSchema = z.object({
  kind: CompareKind,
  tz: z.string().nullable().optional(),
})
export const FrameCompareSchema = z.object({
  kind: CompareKind,
  start: z.string(),
  end: z.string(),
  value: z.number().nullable().optional(),
  pct_change: z.number().nullable().optional(),
  lower_is_better: z.boolean().default(true),
})
export const ScopeSchema = z.object({
  entity_def: z.string(),
  filter: FilterClauseSchema.optional(),
  quantity: z.string().nullable().optional(),
  // SEN-1513 half 2: the TWO-measure wire form — at most two measures on one
  // chart (a cartesian has at most two honest y-axes). Present ONLY for the
  // two-measure form; the single-measure form keeps `quantity`.
  quantities: z.array(z.string()).max(2).nullable().optional(),
  attribute: z.string().nullable().optional(),
  period_start: z.string().datetime({ offset: true }).nullable().optional(),
  period_end: z.string().datetime({ offset: true }).nullable().optional(),
  // GAP A: the shifted window is computed SERVER-side; the client only names
  // which comparison it wants.
  compare: ComparePeriodSchema.nullable().optional(),
})
// GATE: a measure named in the request — a saved metric by slug, or an
// inline formula (SEN-316: a proposed calculated field's served
// `sample_formula`). Its shape is the GENERATED `Measure1` (the server's
// schema); the client passes it through and never re-declares its fields.
export const MeasureSchema = z.custom<Measure1>((v) => typeof v === 'object' && v !== null && typeof (v as { as?: unknown }).as === 'string')

// GATE (spec §6): which page of a long answer — mirrors contract.py PageSpec.
// `size` 1..PAGE_SIZE_MAX (server default 200 when `page` is absent);
// `cursor` is the previous page's `meta.page.cursor`.
export const PAGE_SIZE_MAX = 10_000
export const PageSpecSchema = z.object({
  size: z.number().int().min(1).max(PAGE_SIZE_MAX).optional(),
  cursor: z.string().nullable().optional(),
})

export const ReduceRequestSchema = z.object({
  op: z.literal('reduce').default('reduce'),
  scope: ScopeSchema,
  // The server's cap (contract.py MAX_MEASURES = 16 since SEN-395 be-perf):
  // a collection summary totals every visible metric column in ONE question.
  measures: z.array(MeasureSchema).min(1).max(16).nullable().optional(),
  reducer: ReducerSchema,
  mrl_snapshot_id: z.string().nullable().optional(),
  accurate_at: z.string().datetime({ offset: true }).nullable().optional(),
  page: PageSpecSchema.nullable().optional(),
})

// ── ReducedFrame validator ───────────────────────────────────────────────────
const UcTerm = z.object({
  ref_kind: z.enum(['unit', 'type', 'attribute']),
  ref: z.string(),
  power: z.number().int(),
})
const UnitComposition = z.union([
  z.object({ numerator: z.array(UcTerm), denominator: z.array(UcTerm) }),
  z.object({ mixed: z.literal(true) }),
])
const FrameWarning = z.object({
  kind: WarningKind,
  human: z.string(),
  severity: Severity,
  structured: z.record(z.string(), z.unknown()).nullable().optional(),
})
const DroppedEntry = z.object({
  reason: DroppedReason,
  count: z.number().int().nonnegative(),
  detail: z.string().nullable().optional(),
})
// SEN-1411 (T6/F3): agent-suppliable presentation annotations — rendered at
// EVERY density ("the agent explains in place"). Mirrors contract.py FrameMark.
export const FrameMarkSchema = z.object({
  kind: z.enum(['highlight', 'threshold', 'span', 'caption']),
  row_key: z.string().nullable().optional(),
  value: z.number().nullable().optional(),
  span_from: z.union([z.string(), z.number()]).nullable().optional(),
  span_to: z.union([z.string(), z.number()]).nullable().optional(),
  label: z.string().nullable().optional(),
})
export type FrameMark = z.infer<typeof FrameMarkSchema>
// Owner ruling 2026-10-21 ("return the answer with how it was generated"):
// how an answer was generated — a compact summary of the frame's own warnings
// plus the period read and the factor records priced with. Mirrors
// contract.py FrameProvenance (PR 417). Optional: a frame with no factor
// carries none. Without it here zod's default strip would drop it silently.
const FrameProvenanceFactorSchema = z.object({
  record: z.string(),
  entities: z.number().int().nonnegative().optional(),
  valid_from: z.string().nullable().optional(),
  valid_until: z.string().nullable().optional(),
})
export const FrameProvenanceSchema = z.object({
  coverage: z.enum(['complete', 'partial', 'none']).nullable().optional(),
  covered: z.number().nullable().optional(),
  uncovered: z.number().nullable().optional(),
  uncovered_share: z.number().nullable().optional(),
  entities: z.number().nullable().optional(),
  estimated_share: z.number().nullable().optional(),
  defaulted: z.array(z.string()).optional(),
  excluded: z.array(z.string()).optional(),
  overlaps_resolved: z.array(z.string()).optional(),
  notes: z.array(z.string()).optional(),
  factors: z.array(FrameProvenanceFactorSchema).optional(),
  period_start: z.string().nullable().optional(),
  period_end: z.string().nullable().optional(),
})
export type FrameProvenance = z.infer<typeof FrameProvenanceSchema>
export const FrameColumnSchema = z.object({
  name: z.string(),
  role: z.enum(['dimension', 'measure']),
  unit: z.string().nullable().optional(),
  currency: z.string().nullable().optional(),
  label: z.string().nullable().optional(),
  aggregation: z.string().nullable().optional(),
  // a scalar figure on its unit's display ladder, computed by the SERVER ("276" GWh, not "276M" kWh):
  // shown as given, never scaled here.
  display: z.object({ value: z.number(), unit: z.string() }).nullable().optional().catch(undefined),
})
export type FrameColumn = z.infer<typeof FrameColumnSchema>
const ReducedFrameMeta = z.object({
  dimensions: z.array(z.string()),
  unit_composition: UnitComposition,
  warnings: z.array(FrameWarning),
  dropped: z.array(DroppedEntry),
  accurate_at: z.string(),
  mrl_snapshot_id: z.string(),
  marks: z.array(FrameMarkSchema).nullable().optional(),
  // SEN-1513 half 2: EXTRA per-measure value columns on a wide two-measure
  // frame (beyond `value`, which is always the FIRST measure). Renderers draw
  // one line per named column — they never guess which keys are measures.
  measure_columns: z.array(z.string()).nullable().optional(),
  // SEN-1723: each measure column's unit composition — the renderer draws one
  // axis when they agree dimensionally and a right-hand axis when they do not.
  measure_units: z.record(z.string(), UnitComposition).nullable().optional(),
  // SEN-1733: identity columns that walked a REVERSE reference carry a SET
  // (sorted list of record ids) per row; the frame names them.
  set_columns: z.array(z.string()).nullable().optional(),
  compare: FrameCompareSchema.nullable().optional(),
  // GATE (spec §6): this page of a long answer — present only when the answer
  // is longer than one page. `cursor` fetches the next (null on the last).
  page: z.object({
    offset: z.number().int().nonnegative(),
    size: z.number().int().positive(),
    total_rows: z.number().int().nonnegative(),
    cursor: z.string().nullable().optional(),
  }).nullable().optional(),
  // Tolerant: a provenance shape this GUI does not know (a newer server) is
  // DROPPED, never a parse failure that blanks the whole answer.
  provenance: FrameProvenanceSchema.nullable().optional().catch(undefined),
  // GATE §6 + 2026-10-23: the answer's columns — a measure's own name and the
  // reducer's aggregation, so a reader labels a figure from the calculation,
  // never from its slug. Tolerant like provenance.
  columns: z.array(FrameColumnSchema).nullable().optional().catch(undefined),
})
export const ReducedFrameSchema = z.object({
  rows: z.array(z.record(z.string(), z.unknown())),
  meta: ReducedFrameMeta,
})
